'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { motion, useMotionTemplate, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { useWebsitePagesCopy } from '@/lib/cadrino-landing/useWebsitePagesCopy';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { BRAND_LOGO_HEIGHT } from '@/lib/brand/brand-assets';
import { Container, PrimaryButton } from './shared';
import { cn } from '@/lib/utils';
import { easeOut } from '@/lib/animations';
import { buildMarketingNavLabels, getMarketingNavLinks, isMarketingNavLinkActive, marketingNavLinkClass, marketingNavLinkClassMobile, resolveMarketingNavHref } from '@/lib/cadrino-landing/marketing-nav';
import { useMarketingNavHash } from '@/hooks/useMarketingNavHash';
import { handleLandingAnchorClick } from '@/lib/cadrino-landing/scroll-to-section';
const HEADER_HEIGHT = 64;
export function CadrinoHeader() {
  const c = useCadrinoCopy();
  const pages = useWebsitePagesCopy();
  const {
    language,
    setLanguage
  } = useLandingLanguage();
  const pathname = usePathname();
  const hash = useMarketingNavHash();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const reducedMotion = useReducedMotion();
  const {
    scrollY
  } = useScroll();
  const backgroundColor = useTransform(scrollY, [0, 48, 120], ['rgba(248, 250, 252, 0.72)', 'rgba(248, 250, 252, 0.88)', 'rgba(248, 250, 252, 0.96)']);
  const borderOpacity = useTransform(scrollY, [0, 80], [0, 0.1]);
  const shadowOpacity = useTransform(scrollY, [0, 100], [0, 0.35]);
  const navMutedOpacity = useTransform(scrollY, [0, 60], [0.72, 1]);
  const borderColor = useMotionTemplate`rgba(148, 163, 184, ${borderOpacity})`;
  const boxShadow = useMotionTemplate`0 1px 0 rgba(148,163,184,0.14), 0 8px 24px rgba(15,23,42,${shadowOpacity})`;
  useMotionValueEvent(scrollY, 'change', y => {
    setScrolled(y > 12);
  });
  const links = getMarketingNavLinks(buildMarketingNavLabels(pages.nav, {
    how: c.nav.how,
    contact: c.nav.contact
  }));
  const headerClass = cn('fixed inset-x-0 top-0 z-50 supports-[backdrop-filter]:backdrop-blur-xl', open && 'bg-cadrino-bg/95 backdrop-blur-xl');
  const headerStyle = open || reducedMotion ? undefined : {
    backgroundColor,
    borderBottomColor: borderColor,
    boxShadow
  };
  return <>
      <motion.header className={headerClass} style={headerStyle} initial={reducedMotion ? false : {
      y: -12,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.45,
      ease: easeOut
    }}>
        <motion.div className="border-b border-transparent" style={open || reducedMotion ? {
        borderBottomColor: 'rgba(255,255,255,0.08)'
      } : {
        borderBottomColor: borderColor
      }}>
          <Container className="flex h-16 items-center justify-between">
            <BrandLogo href="/" variant="forLightBg" height={BRAND_LOGO_HEIGHT.header} priority />

            <motion.nav className="hidden items-center gap-8 md:flex" style={reducedMotion ? undefined : {
            opacity: navMutedOpacity
          }}>
              {links.map(l => {
              const active = isMarketingNavLinkActive(pathname, hash, l);
              const href = resolveMarketingNavHref(l, pathname);
              const className = marketingNavLinkClass(active);
              if (l.isAnchor && href.startsWith('#')) {
                return <a key={l.id} href={href} className={className} aria-current={active ? 'page' : undefined} onClick={e => handleLandingAnchorClick(e, href)}>
                      {l.label}
                    </a>;
              }
              return <Link key={l.id} href={href} className={className} aria-current={active ? 'page' : undefined}>
                    {l.label}
                  </Link>;
            })}
            </motion.nav>

            <div className="hidden items-center gap-4 md:flex">
              <div className={cn('flex rounded-xl border p-0.5 text-xs transition-colors duration-300', scrolled ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50')}>
                {(['sr', 'en'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} className={cn('min-w-[2.1rem] rounded-lg px-2.5 py-1 font-medium uppercase transition-colors', language === lang ? 'bg-slate-200 text-slate-900' : 'text-cadrino-muted hover:text-slate-700')}>
                    {lang}
                  </button>)}
              </div>
              <Link href="/login" className="px-1.5 text-sm font-medium text-cadrino-muted transition-colors hover:text-slate-900">
                {c.nav.signIn}
              </Link>
              <PrimaryButton href="#contact" className="h-9 whitespace-nowrap px-5 text-xs shadow-sm">
                {c.nav.bookDemo}
              </PrimaryButton>
            </div>

            <button type="button" className="text-slate-700 md:hidden" onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </Container>
        </motion.div>

        <motion.div className="overflow-hidden md:hidden" initial={false} animate={{
        height: open ? 'auto' : 0,
        opacity: open ? 1 : 0
      }} transition={{
        duration: reducedMotion ? 0.01 : 0.28,
        ease: easeOut
      }}>
          <div className="border-t border-slate-200 bg-cadrino-surface/95 backdrop-blur-xl">
            <Container className="flex flex-col gap-4 py-4">
              {links.map(l_0 => {
              const active_0 = isMarketingNavLinkActive(pathname, hash, l_0);
              const href_0 = resolveMarketingNavHref(l_0, pathname);
              const className_0 = marketingNavLinkClassMobile(active_0);
              if (l_0.isAnchor && href_0.startsWith('#')) {
                return <a key={l_0.id} href={href_0} className={className_0} aria-current={active_0 ? 'page' : undefined} onClick={e_0 => {
                  handleLandingAnchorClick(e_0, href_0);
                  setOpen(false);
                }}>
                      {l_0.label}
                    </a>;
              }
              return <Link key={l_0.id} href={href_0} className={className_0} aria-current={active_0 ? 'page' : undefined} onClick={() => setOpen(false)}>
                    {l_0.label}
                  </Link>;
            })}
              <div className="flex gap-2">
                {(['sr', 'en'] as const).map(lang_0 => <button key={lang_0} type="button" onClick={() => setLanguage(lang_0)} className={cn('rounded-lg border border-slate-300 px-3 py-1.5 text-xs uppercase', language === lang_0 && 'bg-slate-200')}>
                    {lang_0}
                  </button>)}
              </div>
              <Link href="/login" className="text-sm font-medium text-cadrino-muted transition-colors hover:text-slate-900" onClick={() => setOpen(false)}>
                {c.nav.signIn}
              </Link>
              <PrimaryButton href="#contact" onClick={() => setOpen(false)}>
                {c.nav.bookDemo}
              </PrimaryButton>
            </Container>
          </div>
        </motion.div>
      </motion.header>

      {/* Reserve space so content does not sit under fixed header */}
      <div className="shrink-0" style={{
      height: HEADER_HEIGHT
    }} aria-hidden />
    </>;
}